import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import UserCircleIcon from 'components/v2/icons/UserCircleIcon';
import UserGroupIcon from 'components/v2/icons/UserGroupIcon';
import VerticalNav from 'components/v2/navs/VerticalNav';
import BellIcon from 'components/v2/icons/BellIcon';
import SquaresPlusIcon from 'components/v2/icons/SquarePlusIcon';
import ChatAltIcon from 'components/v2/icons/ChatAltIcon';
import TruckIcon from 'components/v2/icons/TruckIcon';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { getIsNewFscFlow } from 'utils/FscSettingsApiUtils';

const Sidebar = ({ userId, activePage, t }) => {
  const [isNewFscFlow, setIsNewFscFlow] = useState(null);

  function isActive(page) {
    return activePage === page;
  }

  useEffect(() => {
    (async () => {
      const isNewFscFlow = await getIsNewFscFlow();
      setIsNewFscFlow(isNewFscFlow);
    })();
  }, []);

  const profileActive = isActive('profile');
  const ratesActive = isActive('rates');
  const integrationsActive = isActive('integrations');
  const driversActive = isActive('drivers');
  const notificationsActive = isActive('notifications');
  const activityActive = isActive('activity');
  const billingActive = isActive('billing');
  const quickpayActive = isActive('quickpay');
  const teamActive = isActive('team');

  return (
    <VerticalNav>
      <VerticalNav.NavItemWithIcon
        url={`/users/${userId}`}
        active={profileActive}
        title={titleize(t('PROFILE', { count: 1 }))}
      >
        {(iconClassName) => (
          <UserCircleIcon size="medium" className={iconClassName} />
        )}
      </VerticalNav.NavItemWithIcon>
      <VerticalNav.NavItemWithIcon
        url={`/users/${userId}/carrier-settings/drivers`}
        active={driversActive}
        title={titleize(t('DRIVER', { count: 2 }))}
      >
        {(iconClassName) => (
          <TruckIcon size="medium" className={iconClassName} />
        )}
      </VerticalNav.NavItemWithIcon>
      <VerticalNav.NavItemWithIcon
        url={`/users/${userId}/carrier-settings/team`}
        active={teamActive}
        title={t('TEAM')}
      >
        {(iconClassName) => (
          <UserGroupIcon type="outline" className={iconClassName} />
        )}
      </VerticalNav.NavItemWithIcon>
      <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
        <VerticalNav.NavItemWithIcon
          url={`/users/${userId}/carrier-settings/billing`}
          active={billingActive}
          title={t('BILLING')}
        >
          {(iconClassName) => (
            <SquaresPlusIcon type="outline" className={iconClassName} />
          )}
        </VerticalNav.NavItemWithIcon>
      </AuthorizationWrapper>
      <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
        <VerticalNav.NavItemWithIcon
          url={`/users/${userId}/carrier-settings/quick-pay`}
          active={quickpayActive}
          title={t('QUICK_PAY')}
        >
          {(iconClassName) => (
            <SquaresPlusIcon type="outline" className={iconClassName} />
          )}
        </VerticalNav.NavItemWithIcon>
      </AuthorizationWrapper>
      {isNewFscFlow && (
        <VerticalNav.NavItemWithIcon
          url={`/users/${userId}/settings/rates`}
          active={ratesActive}
          title={t('RATES_SETTINGS')}
        >
          {(iconClassName) => (
            <CurrencyDollarIcon type="outline" className={iconClassName} />
          )}
        </VerticalNav.NavItemWithIcon>
      )}
      <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
        <VerticalNav.NavItemWithIcon
          url={`/users/${userId}/carrier-settings/integrations`}
          active={integrationsActive}
          title={t('INTEGRATION', { count: 2 })}
        >
          {(iconClassName) => (
            <SquaresPlusIcon type="outline" className={iconClassName} />
          )}
        </VerticalNav.NavItemWithIcon>
      </AuthorizationWrapper>
      <VerticalNav.NavItemWithIcon
        url={`/users/${userId}/carrier-settings/notifications`}
        active={notificationsActive}
        title={titleize(t('NOTIFICATION', { count: 2 }))}
      >
        {(iconClassName) => (
          <BellIcon size="medium" className={iconClassName} />
        )}
      </VerticalNav.NavItemWithIcon>
      <VerticalNav.NavItemWithIcon
        url={`/users/${userId}/carrier-settings/activity`}
        active={activityActive}
        title={titleize(t('ACTIVITY'))}
      >
        {(iconClassName) => (
          <ChatAltIcon type="outline" className={iconClassName} />
        )}
      </VerticalNav.NavItemWithIcon>
    </VerticalNav>
  );
};

Sidebar.propTypes = {
  userId: PropTypes.string,
  activePage: PropTypes.string,
};

Sidebar.defaultProps = {
  activePage: 'profile',
};

export default withTranslation()(Sidebar);
